import React from "react";
import { Navigate, Outlet } from "react-router-dom";
import { isRoleSessionActive } from "../utils/auth";

/**
 * ProtectedRoute
 *
 * Synchronously checks auth BEFORE rendering any children.
 * No flash-of-unauthorized-content.
 *
 * Props:
 *   allowedRole — "admin" | "engineer" | "accounts"
 *
 * Behaviour:
 *   - Not logged in at all → redirect to /{allowedRole}-login
 *   - Logged in but wrong role → redirect to their actual dashboard
 *   - Correct role → render <Outlet />
 */

const ROLE_DASHBOARDS = {
  admin: "/dashboard",
  engineer: "/engineer-dashboard",
  accounts: "/accounts-dashboard",
  lg_admin: "/attendance/lg-admin",
};

const ROLE_LOGINS = {
  admin: "/admin-login",
  engineer: "/engineer-login",
  accounts: "/accounts-login",
  lg_admin: "/attendance/login/lg_admin",
};

export default function ProtectedRoute({ allowedRole }) {
  const token = localStorage.getItem("token");
  const storedRole = localStorage.getItem("role");

  // No token at all — not logged in
  if (!token) {
    return <Navigate to={ROLE_LOGINS[allowedRole] || "/"} replace />;
  }

  // Has token but role session is not active for the required role
  if (!isRoleSessionActive(allowedRole)) {
    // If they have an active session for a different role, send them there
    const activeRole = ["admin", "engineer", "accounts", "lg_admin"].find((r) =>
      isRoleSessionActive(r)
    );
    if (activeRole) {
      return <Navigate to={ROLE_DASHBOARDS[activeRole]} replace />;
    }
    // Session invalid/expired — send to login
    return <Navigate to={ROLE_LOGINS[allowedRole] || "/"} replace />;
  }

  // Role mismatch (extra safety check)
  if (storedRole && storedRole !== allowedRole) {
    const activeDashboard = ROLE_DASHBOARDS[storedRole];
    if (activeDashboard) {
      return <Navigate to={activeDashboard} replace />;
    }
  }

  return <Outlet />;
}
